import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from 'app/hooks';
import { classNames } from 'utils/styleUtils';
import { fetchGitlabGroups, type IGitlabGroup, type IGroupMapping } from '../reducers/scimManagementSlice';

interface GroupMappingEditorProps {
  mappings: IGroupMapping[];
  onChange: (mappings: IGroupMapping[]) => void;
  gitlabGroups: IGitlabGroup[];
  gitlabGroupsLoading: boolean;
  platformToken: string | null;
}

const ACCESS_LEVELS = ['GUEST', 'REPORTER', 'DEVELOPER', 'MAINTAINER', 'OWNER'];

const SEARCH_DEBOUNCE_MS = 300;

const GroupMappingEditor = ({
  mappings,
  onChange,
  gitlabGroups,
  gitlabGroupsLoading,
  platformToken,
}: GroupMappingEditorProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [newMapping, setNewMapping] = useState<IGroupMapping>({
    gitlab_group_id: 0,
    gitlab_group_path: null,
    access_level: 'DEVELOPER',
    enabled: true,
  });
  const [query, setQuery] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (platformToken === null) return;
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void dispatch(fetchGitlabGroups({ platformToken, search: query }));
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, [dispatch, platformToken, query]);

  const handleAdd = (): void => {
    if (newMapping.gitlab_group_id !== 0) {
      onChange([...mappings, newMapping]);
      setNewMapping({ gitlab_group_id: 0, gitlab_group_path: null, access_level: 'DEVELOPER', enabled: true });
      setQuery('');
    }
  };

  const handleToggle = (index: number): void => {
    const updated = mappings.map((m, i) => (i === index ? { ...m, enabled: !m.enabled } : m));
    onChange(updated);
  };

  const handleRemove = (index: number): void => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  const groupLabel = (m: IGroupMapping): string => {
    if (m.gitlab_group_path != null) return `${m.gitlab_group_id} (${m.gitlab_group_path})`;
    return String(m.gitlab_group_id);
  };

  const selectedGitlabGroup = gitlabGroups.find((g) => g.id === newMapping.gitlab_group_id) ?? null;

  return (
    <div className="space-y-2">
      {mappings.length > 0 && (
        <table className="w-full text-sm divide-y divide-gray-200">
          <thead>
            <tr className="bg-dataops-dark text-white">
              <th className="px-3 py-2 text-left">DataOps Group</th>
              <th className="px-3 py-2 text-left">Access Level</th>
              <th className="px-3 py-2 text-left">Enabled</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {mappings.map((m, i) => (
              <tr key={i} className="even:bg-gray-50">
                <td className="px-3 py-2">{groupLabel(m)}</td>
                <td className="px-3 py-2">{m.access_level}</td>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={m.enabled}
                    onChange={() => handleToggle(i)}
                    className="rounded border-gray-300"
                    aria-label={`Toggle enabled for mapping ${m.gitlab_group_id}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => handleRemove(i)} aria-label="Remove mapping">
                    <TrashIcon className="h-4 w-4 text-red-highlight hover:text-red-700" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {platformToken !== null ? (
          <Combobox
            value={selectedGitlabGroup}
            onChange={(g: IGitlabGroup | null) =>
              setNewMapping({
                ...newMapping,
                gitlab_group_id: g?.id ?? 0,
                gitlab_group_path: g?.full_path ?? null,
              })
            }
          >
            <div className="relative flex-1 min-w-0">
              <div className="relative overflow-hidden rounded border border-gray-300 bg-white text-sm flex items-center">
                <Combobox.Input
                  className="w-full py-1 pl-2 pr-8 text-sm text-primary-text focus:outline-none"
                  displayValue={(g: IGitlabGroup | null) => (g != null ? `${g.id} (${g.full_path})` : '')}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Select a DataOps group…"
                  aria-label="Select DataOps Group"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {gitlabGroupsLoading ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-secondary-text">Searching…</div>
                  ) : gitlabGroups.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-secondary-text">
                      No groups found.
                    </div>
                  ) : (
                    gitlabGroups.map((g) => (
                      <Combobox.Option
                        key={g.id}
                        value={g}
                        className={({ active }) =>
                          classNames(
                            active ? 'bg-gray-100 text-primary-text' : 'text-primary-text',
                            'relative cursor-default select-none py-2 pl-3 pr-9',
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={classNames(selected ? '' : 'font-normal', 'block truncate')}>
                              {g.id} <span className="text-secondary-text">({g.full_path})</span>
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-dataops-primary-blue">
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </Combobox>
        ) : (
          <input
            type="number"
            placeholder="GitLab Group ID"
            value={newMapping.gitlab_group_id === 0 ? '' : newMapping.gitlab_group_id}
            onChange={(e) => setNewMapping({ ...newMapping, gitlab_group_id: Number(e.target.value) })}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-36"
            aria-label="New GitLab Group ID"
          />
        )}
        <select
          value={newMapping.access_level}
          onChange={(e) => setNewMapping({ ...newMapping, access_level: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          aria-label="New access level"
        >
          {ACCESS_LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-sm text-secondary-text">
          <input
            type="checkbox"
            checked={newMapping.enabled}
            onChange={(e) => setNewMapping({ ...newMapping, enabled: e.target.checked })}
            className="rounded border-gray-300"
            aria-label="New mapping enabled"
          />
          Enabled
        </label>
        <button
          onClick={handleAdd}
          disabled={newMapping.gitlab_group_id === 0}
          className="flex items-center gap-1 bg-dataops-primary-blue hover:bg-hover-primary-blue text-white rounded px-3 py-1 text-sm disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
};

export default GroupMappingEditor;
